import { memo } from 'react'
import { Check, Circle } from 'lucide-react'
import type { Program } from '../../types/index.js'
import { programReadiness } from './readiness.js'
import type { ProgramPartsBridge } from './ProgramForm.js'
import { MediaAssetManager } from './MediaAssetManager.js'
import { formatTime, formatDateShort, formatDuration, coverageColor } from '../../lib/utils.js'

// ─── Props ────────────────────────────────────────────────────────────────────

interface ProgramSidebarProps {
  program:      Program
  onAddToGrid?: (program: Program) => void
  /** Navigation des parties + aperçu EPG publiés par la fiche centrale. */
  partsBridge?: ProgramPartsBridge | null
  onMediaChanged?: () => void | Promise<void>
  mediaDisabled?: boolean
  mediaDisabledReason?: string
  detailMode?: boolean
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const ProgramSidebar = memo(function ProgramSidebar({ program, onAddToGrid, partsBridge, onMediaChanged, mediaDisabled, mediaDisabledReason, detailMode = false }: ProgramSidebarProps) {
  const stats      = program.stats
  const asRunHistory = program.asRunHistory ?? []
  const assets     = program.mediaAssets ?? []
  const mainAsset  = assets.find(a => !a.isProxy)

  // Score de compatibilité playout (0-100) basé sur les médias
  const compat = computeCompatibility(program)

  // Même règle que le catalogue et l'aperçu : une seule définition de ce qui
  // rend une fiche diffusable, sinon le badge de la liste et celui de la fiche
  // finiraient par se contredire.
  const readiness = programReadiness(program)

  // Un programme découpé ne dure pas ce que dure sa partie 1 : la durée réelle
  // se lit sur l'émission entière, donc sur la somme des parties (le backend la
  // publie en `totalDurationSec`). Les coupures entre parties en sont exclues,
  // c'est de l'écran publicitaire, pas du programme. L'écart moyen mesuré reste
  // celui de la partie 1, seule à porter l'historique AsRun du bundle.
  const contentDurationSec = program.totalDurationSec ?? program.durationSec

  return (
    <div className={`catalog-sidebar${detailMode ? ' catalog-sidebar--detail' : ''}`}>

      {detailMode && (
        <div className="catalog-sidebar-heading">
          <h2>Qualité et diffusion</h2>
          <span>{program.isLive ? 'DIRECT' : mainAsset?.status === 'OK' ? 'PRÊT' : 'À VÉRIFIER'}</span>
        </div>
      )}

      {detailMode && (
        <Section title="Média">
          <MediaAssetManager
            program={program}
            title={program.isLive ? 'Source live' : 'Fichier principal'}
            onChanged={onMediaChanged}
            disabled={mediaDisabled}
            disabledReason={mediaDisabledReason}
          />
        </Section>
      )}

      {/* ── Structure (parties) ──────────────────────────────────────────── */}
      {!detailMode && partsBridge && (
        <Section title="Structure" aside={formatDuration(partsBridge.totalDurationSec)}>
          <div className="catalog-parts" role="group" aria-label="Parties du programme">
            {partsBridge.parts.map((part, index) => (
              <button
                key={part.key}
                type="button"
                className={`catalog-part-row${index === partsBridge.activePartIndex ? ' is-active' : ''}`}
                aria-pressed={index === partsBridge.activePartIndex}
                onClick={() => partsBridge.onSelectPart(index)}
              >
                <span className="catalog-part-row__dot" aria-hidden="true" />
                <span className="catalog-part-row__name">{part.label}</span>
                {part.isNew && <span className="catalog-part-row__new">Nouvelle</span>}
                {part.overrideCount > 0 && (
                  <span
                    className="catalog-part-row__override"
                    aria-label={`${part.overrideCount} champ${part.overrideCount > 1 ? 's' : ''} surchargé${part.overrideCount > 1 ? 's' : ''}`}
                    title={`${part.overrideCount} champ${part.overrideCount > 1 ? 's' : ''} surchargé${part.overrideCount > 1 ? 's' : ''}`}
                  >
                    {part.overrideCount}
                  </span>
                )}
                <span className="catalog-part-row__dur">{formatDuration(part.durationSec)}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            className="catalog-small-action catalog-parts__add"
            onClick={partsBridge.onAddPart}
            disabled={!partsBridge.canAddPart}
          >
            + Ajouter une partie
          </button>
          <div className="catalog-sidebar-legend">
            Pastille = champ surchargé
            {partsBridge.interPartBreakSec > 0 && <> · Coupure entre parties : {formatDuration(partsBridge.interPartBreakSec)}</>}
          </div>
        </Section>
      )}

      {/* ── Prêt pour l'antenne ──────────────────────────────────────────
          Placé au-dessus de l'aperçu EPG : c'est la question à laquelle
          l'opérateur veut répondre en premier, et chaque point nomme ce qui
          manque plutôt que de se contenter d'un état global. */}
      <Section
        title="Prêt pour l’antenne"
        aside={`${readiness.satisfiedRequired} / ${readiness.totalRequired}`}
      >
        <ul className="catalog-readiness">
          {readiness.checks.map(check => (
            <li
              key={check.id}
              className={`catalog-readiness__row${check.satisfied ? ' is-ok' : check.required ? ' is-missing' : ''}`}
            >
              {check.satisfied
                ? <Check size={13} aria-hidden="true" />
                : <Circle size={13} aria-hidden="true" />}
              <span className="catalog-readiness__copy">
                <span className="catalog-readiness__label">{check.label}</span>
                {check.detail && <span className="catalog-readiness__detail">{check.detail}</span>}
              </span>
            </li>
          ))}
        </ul>
      </Section>

      {/* ── Aperçu EPG ───────────────────────────────────────────────────── */}
      {(partsBridge || detailMode) && (
        <Section title="Aperçu EPG">
          <div className="catalog-epg-preview" aria-label="Aperçu des lignes EPG publiées">
            {(partsBridge?.epgPreview ?? [{
              title: program.titleShort || program.title || '—',
              subtitle: program.epgSubtitle || program.summary || 'Aucun résumé EPG',
            }]).map((line, index) => (
              <div key={index} className="catalog-epg-line">
                <span className="catalog-epg-line__title">{line.title || '—'}</span>
                <span className="catalog-epg-line__sub">{line.subtitle}</span>
              </div>
            ))}
          </div>
          <p className="catalog-epg-note">{partsBridge?.epgNote ?? 'Aperçu de la fiche publiée au guide des programmes.'}</p>
        </Section>
      )}

      {/* ── Statistiques ─────────────────────────────────────────────────── */}
      {!detailMode && <Section title="Statistiques">
        <div className="catalog-stat-grid">
          <StatCard label="Diffusions totales" value={String(stats?.totalAirings ?? 0)} />
          <StatCard label="Rediffusions" value={String(stats?.rerunCount ?? program.legacyRerunCount ?? 0)} />
          <StatCard label="Programmées"         value={String(stats?.scheduledCount ?? 0)} />
          <StatCard label="Durée réelle moy."   value={formatDuration(contentDurationSec + (stats?.avgDriftSec ?? 0))} />
          <StatCard
            label="Incidents gap"
            value={String(stats?.gapIncidents ?? 0)}
            danger={(stats?.gapIncidents ?? 0) > 0}
          />
        </div>
        <div className="catalog-sidebar-note">
          Première diffusion : {stats?.firstAiredAt ? formatDateShort(stats.firstAiredAt) : program.plannedFirstAirDate ? `prévue le ${formatDateShort(program.plannedFirstAirDate)}` : program.isNew ? 'à venir' : 'historique à compléter'}
          {stats?.lastAiredAt && <> · Dernière : {formatDateShort(stats.lastAiredAt)}</>}
        </div>
      </Section>}

      {/* ── Prochaines diffusions ─────────────────────────────────────────── */}
      <Section title={detailMode ? 'Prochaine diffusion' : 'Prochaines diffusions'}>
        {program.scheduleItems && program.scheduleItems.length > 0 ? (
          <>
            {program.scheduleItems.slice(0, 5).map(item => (
              <div key={item.id} className="catalog-schedule-row">
                <div className="catalog-schedule-row__dot" />
                <div className="catalog-schedule-row__copy">
                  <div className="catalog-schedule-row__title">
                    {formatDateShort(item.startTime)} — {formatTime(item.startTime)}
                  </div>
                  <div className="catalog-schedule-row__meta">
                    {item.grid?.name ?? 'Grille'} · {formatDuration(item.durationSec)}
                  </div>
                </div>
                <span className="catalog-status-badge catalog-status-badge--success">
                  Prêt
                </span>
              </div>
            ))}
            <button className="tp-button tp-button--secondary catalog-sidebar-action" onClick={() => onAddToGrid?.(program)}>
              + Ajouter à la grille →
            </button>
          </>
        ) : (
          <div className="catalog-empty-copy">
            Aucune diffusion programmée
            <button className="tp-button tp-button--secondary catalog-sidebar-action" onClick={() => onAddToGrid?.(program)}>
              + Ajouter à la grille →
            </button>
          </div>
        )}
      </Section>

      {/* ── Historique AsRun ──────────────────────────────────────────────── */}
      {!detailMode && asRunHistory.length > 0 && (
        <Section title="Historique de diffusion">
          <div className="catalog-history-grid catalog-history-grid--header">
            <span>Date</span><span>Heure</span><span>Durée réelle</span><span>Statut</span>
          </div>
          {asRunHistory.slice(0, 6).map(log => {
            const drift = log.durationDiffSec ?? 0
            const hasGap = Math.abs(drift) > 30
            return (
              <div key={log.id} className="catalog-history-grid catalog-history-grid--row">
                <span className="catalog-history-grid__date">{log.scheduledStart ? formatDateShort(log.scheduledStart) : '—'}</span>
                <span className="catalog-history-grid__time">{log.actualStart ? formatTime(log.actualStart) : '—'}</span>
                <span className={hasGap ? 'catalog-history-grid__duration is-warning' : 'catalog-history-grid__duration'}>
                  {log.actualEnd && log.actualStart
                    ? formatDuration(Math.round((new Date(log.actualEnd).getTime() - new Date(log.actualStart).getTime()) / 1000))
                    : '—'}
                  {hasGap && ` (${drift > 0 ? '+' : ''}${drift}s)`}
                </span>
                <span className={`catalog-status-badge ${log.status === 'OK' ? 'catalog-status-badge--success' : 'catalog-status-badge--danger'}`}>
                  {log.status === 'OK' ? 'OK' : 'Gap'}
                </span>
              </div>
            )
          })}
        </Section>
      )}

      {/* ── Compatibilité playout ─────────────────────────────────────────── */}
      {!detailMode && <Section title="Compatibilité playout">
        {compat.map(item => (
          <div key={item.label} className="catalog-compatibility-row">
            <span className="catalog-compatibility-row__label">{item.label}</span>
            <div className="catalog-compatibility-row__track">
              <div
                className={`catalog-compatibility-row__fill catalog-compatibility-row__fill--${item.tone}`}
                style={{ '--compatibility-score': `${item.score}%` } as React.CSSProperties}
              />
            </div>
            <span className={`catalog-compatibility-row__value catalog-compatibility-row__value--${item.tone}`}>
              {item.text}
            </span>
          </div>
        ))}
      </Section>}
    </div>
  )
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeCompatibility(program: Program) {
  const assets   = program.mediaAssets ?? []
  const main     = assets.find(a => !a.isProxy)
  const normalizeCodec = (c: string) => c.toLowerCase().replace(/[^a-z0-9]/g, '')
  const SUPPORTED = ['DNxHD', 'ProRes', 'H.264', 'H.265', 'HAP', 'h264', 'h265', 'hevc', 'prores', 'dnxhd', 'hap']
  const SUPPORTED_NORM = SUPPORTED.map(normalizeCodec)

  const codecOk = main?.codec ? SUPPORTED_NORM.includes(normalizeCodec(main.codec)) : false
  const durOk   = main?.durationSec
    ? Math.abs(main.durationSec - program.durationSec) < 120
    : false
  const fileOk  = main?.status === 'OK'
  const proxyOk = assets.some(a => a.isProxy && a.status === 'OK')

  return [
    { label: 'Codec',   score: main ? (codecOk ? 100 : 0) : 0, text: !main ? '—' : codecOk ? 'OK' : 'Err', tone: codecOk ? 'success' : 'danger' },
    { label: 'Fichier', score: main ? (fileOk ? 100 : 0) : 0, text: !main ? '—' : fileOk ? 'OK' : 'Err', tone: fileOk ? 'success' : 'danger' },
    { label: 'Durée',   score: main ? (durOk ? 100 : 40) : 0, text: !main ? '—' : durOk ? 'OK' : 'Vér', tone: durOk ? 'success' : 'warning' },
    { label: 'Proxy',   score: proxyOk ? 100 : 0, text: proxyOk ? 'OK' : '—', tone: proxyOk ? 'success' : 'muted' },
  ]
}

// ─── Sous-composants ──────────────────────────────────────────────────────────

const Section = memo(function Section({ title, aside, children }: {
  title: string
  /** Valeur alignée à droite du label (durée totale, compteur…). */
  aside?: string
  children: React.ReactNode
}) {
  return (
    <section className="catalog-sidebar-section">
      <h3 className="catalog-sidebar-section__title">
        {title}
        {aside && <span className="catalog-sidebar-section__aside">{aside}</span>}
      </h3>
      {children}
    </section>
  )
})

const StatCard = memo(function StatCard({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className={danger ? 'catalog-stat-card is-danger' : 'catalog-stat-card'}>
      <div className="catalog-stat-card__value">{value}</div>
      <div className="catalog-stat-card__label">{label}</div>
    </div>
  )
})
